
import Vue from 'vue';
import { mapState, mapActions } from 'vuex';
import {
	RUNNIT_NODE_DEF_TOOL_TYPE,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RUNNIT_TYPE,
	RunnitNodeDef,
	RUNNIT_NODE_DEF_TAG_TYPE_SORT,
	RunnitNodeDefTagType,
} from '@run-diffusion/shared';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { db } from '@/firebase';
import RunnitCarousel from '../RunnitCarousel.vue';
import OfferingCardWithLabels from '@/components/OfferingCardWithLabels.vue';
import { RunnitsCRUDMixin } from '@/mixins/RunnitsCRUDMixin';
import { TeamUserMixin } from '@/mixins';
import { fuzzyFilter } from '@/utils/fuzzySearch';
import RunnitDuplicateEditNodeDefInternalEditor from '../RunnitSettings/internalAdminOnly/RunnitDuplicateEditNodeDefInternalEditor.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import _sortBy from 'lodash/sortBy';
import _isEmpty from 'lodash/isEmpty';
import { RUNNITS_PUBLISHED_STATE, RunnitsPublishedState } from '@/constants/constants';

interface App {
	id: string;
	title: string;
	imageUrl: string;
	avatar: string;
	description: string;
	type: string;
	publishedAt: string;
	newUntil: any;
}

export default Vue.extend({
	name: 'RunnitAppLibrary',
	metaInfo: {
		title: 'Apps',
	},
	mixins: [
		RunnitsCRUDMixin,
		TeamUserMixin,
	],
	data () {
		return {
			RUNNITS_PUBLISHED_STATE,
			searchQuery: '',
			featuredTagId: null,
			appTypeTagIds: [] as string[],
			selectedCategory: 'ALL',
			toolSearchValue: '',
			publishedStateFilter: RUNNITS_PUBLISHED_STATE.PUBLISHED,
			selectedTagsFilterMap: {},
			nodeDefsByTagMap: {},
			nodeDefInternalEditorDialog: {
				open: false,
				selectedNodeDef: null,
			},
		};
	},
	async created () {
		await this.fetchAppTypeTagIds();
		await this.fetchFeaturedTagId();
	},
	mounted () {
		const searchQuery = this.$route.query.search;
		if (searchQuery) {
			this.toolSearchValue = searchQuery as string;
			this.applyFilters();
		}
	},
	computed: {
		...mapState([
			'user',
			'runnitState',
			'recentUserRunnitNodeDefInfos',
			'publicRunnitNodeDefs',
			'companyRunnitNodeDefs',
			'publicRunnitNodeDefTagsMap',
		]),
		tagsOnSelectedType () {
			const excludedTypes: RunnitNodeDefTagType[] = [
				RUNNIT_NODE_DEF_TAG_TYPE.HOME_PAGE,
				RUNNIT_NODE_DEF_TAG_TYPE.INDUSTRY,
				RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_TYPE,
				RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY,
				RUNNIT_NODE_DEF_TAG_TYPE.INTERNAL,
			];
			return this.sortedTagType.map((tagId) => (this.tagsById[tagId])).filter(t => t && (this.user.isAdmin || !excludedTypes.includes(t.type)));
		},
		noFilterTagsSelected () {
			return !Object.keys(this.selectedTagsFilterMap).length ||
				Object.keys(this.selectedTagsFilterMap).every(tagId => !this.selectedTagsFilterMap[tagId]);
		},
		filteredTagTypes () {
			return this.sortedTagType.filter(tagId => this.noFilterTagsSelected ||
				this.selectedTagsFilterMap[tagId]);
		},
		sortedTagType () {
			return _sortBy(Object.keys(this.nodeDefsByTagMap), [
				(tagId) => { return RUNNIT_NODE_DEF_TAG_TYPE_SORT[this._get(this.tagsById, `[${tagId}].type`)] },
				(tagId) => { return this._get(this.tagsById, `[${tagId}].sortOrder`) },
				(tagId) => { return this._get(this.tagsById, `[${tagId}].label`) },
			])
				.filter(tagId =>
					this.selectedCategory === 'ALL' ||
					this.selectedCategory === 'COMPANY' ||
					this._get(this.tagsById, `[${tagId}].type`) === this.selectedCategory ||
					tagId === 'uncategorized'
				);
		},
		tagsById () {
			return {
				'uncategorized': { label: 'Uncategorized', id: 'uncategorized', type: 'uncategorized' },
				...this.publicRunnitNodeDefTagsMap,
			}
		},
		recentApps (): RunnitNodeDef[] {
			return (this.recentUserRunnitNodeDefInfos || []).filter((toolInfo) => (
				!toolInfo.nodeDef.isDeleted &&
				(
					this.user &&
					toolInfo.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT &&
					(
						this.user.isAdmin ||
						toolInfo.nodeDef.isPublished
					)
				) &&
				(
					toolInfo.nodeDef.type !== RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY ||
					(
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.team &&
						toolInfo.nodeDef.companyIds?.[this.team.companyId]
					)
				) &&
				this.appTypeTagIds.some((tagId: string) => toolInfo.nodeDef.tags?.[tagId]) &&
				!this.isNodeDefBlockedForTeamUser(toolInfo.nodeDef)
			)).slice(0, 5).map(toolInfo => ({
				id: toolInfo.nodeDef.id,
				title: toolInfo.nodeDef.title,
				imageUrl: toolInfo.nodeDef.imageUrl,
				avatar: toolInfo.nodeDef.avatar,
				description: toolInfo.nodeDef.description,
				type: toolInfo.nodeDef.type,
				publishedAt: toolInfo.nodeDef.publishedAt,
				newUntil: toolInfo.nodeDef.newUntil,
			}));
		},
		filteredApps (): Partial<RunnitNodeDef>[] {
			const allNodeDefs = [...this.publicRunnitNodeDefs, ...this.companyRunnitNodeDefs];
			const filteredNodeDefs = allNodeDefs.filter((runnitNodeDef: RunnitNodeDef) => {
				let matchesSearch: boolean, matchesPublishedState: boolean, matchesCompany: boolean, matchesAppType: boolean, matchesSelectedTypes: boolean;

				// Check if it's an app type
				matchesAppType = this.appTypeTagIds.some(tagId => runnitNodeDef.tags?.[tagId]);

				if (!this.toolSearchValue) {
					matchesSearch = true;
				} else {
					matchesSearch = fuzzyFilter(
						[runnitNodeDef],
						this.toolSearchValue,
						[
							{ name: 'title', weight: 0.7 },
							{ name: 'description', weight: 0.3 }
						]
					).length > 0;
				}

				matchesPublishedState = !!(
					(this.publishedStateFilter === RUNNITS_PUBLISHED_STATE.PUBLISHED && runnitNodeDef.publishedAt) ||
					(this.publishedStateFilter === RUNNITS_PUBLISHED_STATE.NOT_PUBLISHED && !runnitNodeDef.publishedAt)
				);

				if (this.selectedCategory === 'COMPANY') {
					matchesSelectedTypes = runnitNodeDef.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY;
				} else if (this.selectedCategory && this.selectedCategory !== 'ALL') {
					matchesSelectedTypes = !!(
						_isEmpty(runnitNodeDef.tagTypes) ||
						runnitNodeDef.tagTypes[this.selectedCategory] ||
						Object.values(runnitNodeDef.tagTypes).every(applied => !applied)
					);
				} else {
					matchesSelectedTypes = true;
				}

				matchesCompany = !!(
					runnitNodeDef.type !== RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY ||
					(
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.team?.companyId &&
						runnitNodeDef.companyIds?.[this.team.companyId]
					)
				);

				const matchesNotBlocked: boolean = !this.isNodeDefBlockedForTeamUser(runnitNodeDef);
				return matchesSearch && matchesPublishedState && matchesCompany && matchesAppType && matchesSelectedTypes && matchesNotBlocked;
			});

			return filteredNodeDefs.map(nodeDef => ({
				id: nodeDef.id,
				title: nodeDef.title,
				imageUrl: nodeDef.imageUrl,
				avatar: nodeDef.avatar,
				description: nodeDef.description,
				type: nodeDef.type,
				publishedAt: nodeDef.publishedAt,
				newUntil: nodeDef.newUntil,
			}));
		},
		featuredApps (): Partial<RunnitNodeDef>[] {
			const allNodeDefs = [...this.publicRunnitNodeDefs, ...this.companyRunnitNodeDefs];
			return allNodeDefs
				.filter((nodeDef: RunnitNodeDef) => {
					// Must be an app type
					const isApp: boolean = this.appTypeTagIds.some((tagId: string) => nodeDef.tags?.[tagId]);
					// Must have the featured tag
					const isFeatured: boolean = !!(this.featuredTagId && nodeDef.tags?.[this.featuredTagId]);
					// Must be published for non-admin users
					const isPublished: boolean = !!(this.user.isAdmin || !!nodeDef.publishedAt);
					// Check company access - show public apps OR company apps that match the current team
					const matchesCompany: boolean = !!(
						nodeDef.type !== RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY ||
						(
							this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
							this.team?.companyId &&
							nodeDef.companyIds?.[this.team.companyId]
						)
					);
					const matchesNotBlocked: boolean = !this.isNodeDefBlockedForTeamUser(nodeDef);

					return isApp && isFeatured && isPublished && matchesCompany && matchesNotBlocked && !nodeDef.isDeleted;
				})
				.map(nodeDef => ({
					id: nodeDef.id,
					title: nodeDef.title,
					imageUrl: nodeDef.imageUrl,
					avatar: nodeDef.avatar,
					description: nodeDef.description,
					type: nodeDef.type,
					publishedAt: nodeDef.publishedAt,
					newUntil: nodeDef.newUntil,
				}));
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		handleCategoryChange (selectedCategory: RunnitNodeDefTagType) {
			this.selectedCategory = selectedCategory;
			this.selectedTagsFilterMap = {};
			this.applyFilters();
		},
		handleSearchInput (searchValue) {
			this.$router.replace({ query: { ...this.$route.query, search: searchValue } });
			this.toolSearchValue = searchValue;
			this.applyFilters();
		},
		handlePublishedFilterChange (publishedState: RunnitsPublishedState) {
			this.publishedStateFilter = publishedState;
			this.applyFilters();
		},
		toggleTagFilter (tag) {
			this.selectedTagsFilterMap = {
				...this.selectedTagsFilterMap,
				[tag.id]: !this.selectedTagsFilterMap[tag.id],
			};
		},
		async mapNodeDefsToTag (nodeDefs) {
			this.nodeDefsByTagMap = (nodeDefs || []).reduce((map, nodeDef) => {
				Object.keys(nodeDef.tags || []).map((tagId) => {
					if (!map[tagId]) {
						map[tagId] = [];
					}
					map[tagId].push(nodeDef);
				})
				if (!nodeDef.tags || _isEmpty(nodeDef.tags)) {
					if (!map['uncategorized']) {
						map['uncategorized'] = [];
					}
					map['uncategorized'].push(nodeDef);
				}
				return map;
			}, {});
		},
		applyFilters () {
			const allNodeDefs = [...this.publicRunnitNodeDefs, ...this.companyRunnitNodeDefs];
			this.mapNodeDefsToTag(allNodeDefs);
		},
		async onAppClick (nodeDef: RunnitNodeDef) {
			await this.onAddRunnitClick(nodeDef, RUNNIT_TYPE.SINGLE_TOOL);
		},
		async fetchAppTypeTagIds () {
			const tags = await db.collection('runnitNodeDefTags').where('type', '==', RUNNIT_NODE_DEF_TAG_TYPE.INTERNAL).where('label', '==', 'App').where('isDeleted', '==', false).get();
			this.appTypeTagIds = tags.docs.map(doc => doc.id);
		},
		async fetchFeaturedTagId () {
			try {
				const tags = await db.collection('runnitNodeDefTags')
					.where('type', '==', RUNNIT_NODE_DEF_TAG_TYPE.HOME_PAGE)
					.where('label', '==', 'Featured')
					.where('isDeleted', '==', false)
					.get();

				if (!tags.empty) {
					this.featuredTagId = tags.docs[0].id;
				}
			} catch (error) {
				console.error('Error fetching featured tag:', error);
				this.updateSnackbar({
					status: 'error',
					message: 'Failed to load featured apps. Please try again.',
					show: true,
				});
			}
		},
		async toggleAdminNodeDefEditor (selectedNodeDef: RunnitNodeDef) {
			try {
				// Fetch the complete nodeDef data
				const nodeDefRef = db.doc(`runnitNodeDefs/${selectedNodeDef.id}`);
				const nodeDefDoc = await nodeDefRef.get();
				if (!nodeDefDoc.exists) {
					throw new Error('Node definition not found');
				}

				const completeNodeDef = {
					...nodeDefDoc.data(),
					id: nodeDefDoc.id,
				};

				this.nodeDefInternalEditorDialog = {
					...this.nodeDefInternalEditorDialog,
					open: true,
					selectedNodeDef: completeNodeDef,
				};
			} catch (error) {
				console.error('Error fetching node definition:', error);
				this.updateSnackbar({
					status: 'error',
					message: 'Failed to load tool data. Please try again.',
					show: true,
				});
			}
		},
		onEditDialogClose (open: boolean) {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: !!open,
			};
			this.applyFilters();
		},
		getFullNodeDefForApp (app: Partial<RunnitNodeDef>): RunnitNodeDef | null {
			// Find the full RunnitNodeDef from the state
			const allNodeDefs = [...this.publicRunnitNodeDefs, ...this.companyRunnitNodeDefs];
			return allNodeDefs.find(nodeDef => nodeDef.id === app.id) || null;
		},
	},
	components: {
		RunnitCarousel,
		OfferingCardWithLabels,
		RunnitDuplicateEditNodeDefInternalEditor,
		GlassButton,
	},
});
